import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { LanguageProvider, useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PublicLanguageSwitcher from '../components/public/PublicLanguageSwitcher';
import { Search, MapPin, Calendar, Utensils, User, Globe, Users, ArrowLeft, X, Bell, AlertTriangle, Volume2, BellRing, Map, Camera, Play, Pause } from 'lucide-react';
import { format } from 'date-fns';
import { enUS, pt, fr, es, ru, it } from 'date-fns/locale';
import { getTheme, buildCustomTheme } from '../config/colorThemes';
import { toast } from 'sonner';
import { PublicFloorPlan } from '../components/FloorPlan';
import WishActionBar from '../components/Wishes/WishActionBar';
import { 
  isPushSupported, 
  registerServiceWorker, 
  requestNotificationPermission, 
  subscribeToPush,
  isIOS,
  isIOSSafari,
  isStandalonePWA
} from '../utils/pushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification sound (simple beep using Web Audio API)
const playNotificationSound = () => {
  try {
    // Resume audio context on user interaction
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }
    
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start();
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (e) {
    console.log('Could not play notification sound');
  }
};

// Vibrate device
const vibrateDevice = () => {
  if (navigator.vibrate) {
    navigator.vibrate([200, 100, 200]);
  }
};

// Map language codes to date-fns locales
const dateLocales = {
  en: enUS,
  pt: pt,
  fr: fr,
  es: es,
  ru: ru,
  it: it
};


// ─── Table Voice Note Player for Public Page ─────────────────────
// Same card as text table info, but with tappable voice inside
function TableVoiceNotePlayer({ voiceUrl, colors }) {
  const audioRef = useRef(null);
  const [phase, setPhase] = useState('idle');

  const handleTap = (e) => {
    e.stopPropagation();
    if (phase === 'idle') {
      audioRef.current?.play().then(() => setPhase('playing')).catch(() => {});
    } else {
      audioRef.current?.pause();
      if (audioRef.current) audioRef.current.currentTime = 0;
      setPhase('idle');
    }
  };

  return (
    <div
      className="mt-6 p-4 rounded-xl text-left cursor-pointer select-none"
      style={{ backgroundColor: `${colors.accent}10`, border: `1px solid ${colors.accent}30` }}
      onClick={handleTap}
      data-testid="table-voice-note-player"
    >
      <audio ref={audioRef} src={voiceUrl} onEnded={() => setPhase('idle')} preload="auto" />
      <div className="flex items-start gap-3">
        <svg className="w-6 h-5 flex-shrink-0 mt-0.5" viewBox="0 0 61.85 50.81" xmlns="http://www.w3.org/2000/svg">
          <path fill="#999999" d="M24.61,7.44c-.43,3.92-.92,7.84-1.36,11.77-.45,4.54-1.04,9.08-1.55,13.62-.56,5.17-1.16,10.33-1.76,15.5,7.72,0,15.43,0,23.15,0-.65-4.81-1.34-9.62-2.05-14.42-.71-5.36-1.54-10.7-2.28-16.05-.07-.4-.11-.8-.14-1.2l-.07-.11c-.33-3.04-.87-6.07-1.28-9.1-4.22,0-8.44,0-12.65,0ZM2.58,2.48c.1.73.22,1.41.6,2.05.12.17.19.35.42.38.56.08,1.13.03,1.69.04,16.97,0,33.94,0,50.92,0,.75,0,1.49.02,2.23-.06.48-.77.72-1.51.82-2.41-18.89,0-37.79,0-56.69,0M0,0h61.83c-.01,1.01.06,1.95-.12,2.93-.19,1.41-.65,2.93-1.8,3.86-.57.5-1.28.66-2.03.65-6.04,0-12.08,0-18.12,0,.31,2.08.59,4.16.88,6.24.47,3.53,1.03,7.05,1.52,10.58.65,4.94,1.43,9.86,2.1,14.8.52,3.92,1.14,7.83,1.67,11.75-9.6,0-19.19.02-28.79,0,.2-1.16.25-2.35.43-3.51.49-4.83,1.15-9.65,1.64-14.48.6-5.43,1.28-10.85,1.86-16.28.09-.55.11-1.12.2-1.67.25-2.48.54-4.95.83-7.42-5.83,0-11.66,0-17.49,0-.76,0-1.51.06-2.21-.3-.96-.51-1.56-1.46-1.91-2.47C-.01,3.14-.02,1.6,0,0"/>
        </svg>
        <div>
          <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Table Info:</p>
          {phase === 'idle' ? (
            <p className="text-sm font-medium" style={{ color: colors.text }}>
              In their voice <span style={{ opacity: 0.4 }}>&rarr;</span>
            </p>
          ) : (
            <div className="flex items-center gap-2">
              <WaveformBars color={colors.text} />
              <span className="text-xs" style={{ color: colors.textMuted }}>Playing...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Voice Note Player for Public Page ───────────────────────────
// Couple-recorded personal note FOR this guest. Tap → soft fullscreen overlay
// with quiet "▶ Play" + "For you, when you're ready" — no heavy CTA, no waveform.
function VoiceNotePlayer({ voiceUrl, colors }) {
  const audioRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(false);

  const closeOverlay = () => {
    try {
      audioRef.current?.pause();
      if (audioRef.current) audioRef.current.currentTime = 0;
    } catch { /* */ }
    setPlaying(false);
    setOpen(false);
  };

  const togglePlay = (e) => {
    e?.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); return; }
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  return (
    <>
      <div
        className="mt-4 p-4 rounded-xl text-left cursor-pointer select-none"
        style={{ backgroundColor: `${colors.primary}15`, border: `1px dashed ${colors.primary}50` }}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid="voice-note-player"
      >
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" viewBox="195 195 70 80" xmlns="http://www.w3.org/2000/svg">
            <path fill="#999999" d="M237.7,236.1c.1-.7.1-1.4.1-2.2,0-6.2,0-12.4,0-18.6,0-1.1,0-2.2,0-3.2-.2-1.4-.7-2.7-1.3-4-1.8-3.2-5.3-5.7-9.2-6.3-1.9-.3-4-.3-5.8.2-2.8.7-5.3,2.2-7,4.3-1.4,1.7-2.2,3.7-2.5,5.7,0,.9,0,1.9,0,2.8,0,5.1,0,10.2,0,15.3,0,1.7,0,3.4,0,5.1.4,4.2,3.5,8.1,7.8,9.8,2,.8,4.4,1.1,6.6.9,3.1-.3,6-1.6,8.1-3.7,1.8-1.8,3-3.9,3.4-6.3ZM231.1,200.5c4.1,1.6,7.2,4.8,8.6,8.5.5,1.4.7,2.9.8,4.4,0,6.7,0,13.4,0,20.1,0,.7-.1,1.4-.1,2.2-.4,3.7-2.6,7.4-5.9,9.7-3.3,2.4-7.7,3.5-11.9,2.9-4.7-.6-9.1-3.3-11.3-7.1-1.2-2-2-4.1-2-6.4,0-.5,0-1,0-1.5,0-6.5,0-13,0-19.5,0-.3,0-.7,0-1,0-1.6.4-3.2,1.1-4.7,1.7-3.8,5.5-7,10-8.1,3.5-1,7.3-.7,10.7.6"/>
            <path fill="#999999" d="M205.1,226.9c.6,0,1.1.6,1,1.1,0,2.1,0,4.3,0,6.4,0,2.9.8,5.7,2.2,8.2,2.7,5.1,7.9,8.7,13.5,9.6,3,.5,6.2.3,9.1-.7,2.7-.9,5.3-2.5,7.3-4.5,2.7-2.7,4.6-6.2,5.3-9.9.2-.9.2-1.9.3-2.8,0-2.2,0-4.4,0-6.6,0-.5.5-1,1-1,.4,0,.8.3,1,.6.2.3.1.6.1,1,0,1.8,0,3.7,0,5.5,0,1.6-.2,3.2-.6,4.7-.3,1.2-.7,2.3-1.1,3.4-1.1,2.5-2.7,4.8-4.7,6.7-3.6,3.5-8.5,5.6-13.6,5.8,0,3.6,0,7.1,0,10.7,4.9,0,9.8,0,14.7,0,.5,0,1,.1,1.2.6.4.7-.1,1.5-.9,1.6-10.7,0-21.3,0-32,0-.7,0-1.3-.7-1.1-1.3.1-.5.6-.9,1.1-.9,4.7,0,9.4,0,14.1,0,.3,0,.6,0,.9,0,0-.8,0-1.6,0-2.4,0-2.8,0-5.6,0-8.4-.4,0-.9,0-1.3-.1-6.7-.7-13-4.9-16.2-10.9-1.5-2.8-2.4-6-2.5-9.2,0-2.2,0-4.4,0-6.6,0-.5.6-1.1,1.1-1Z"/>
          </svg>
          <div>
            <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>Personal Note:</p>
            <p className="text-sm font-medium" style={{ color: colors.text }}>
              In their voice <span style={{ opacity: 0.4 }}>&rarr;</span>
            </p>
          </div>
        </div>
      </div>

      {/* Soft overlay — quiet ▶ Play + intimate sub-copy.
          Portaled to document.body so backdrop-filter ancestors can't clip it. */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-8"
          style={{
            backgroundColor: 'rgba(0,0,0,0.78)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            animation: 'voiceNoteOverlayFade 320ms ease',
          }}
          onClick={closeOverlay}
          data-testid="voice-note-overlay"
        >
          <button
            onClick={closeOverlay}
            className="absolute top-5 right-5 text-white/30 hover:text-white/60 transition-opacity"
            data-testid="voice-note-overlay-close"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <div
            className="flex flex-col items-center text-center"
            onClick={(e) => e.stopPropagation()}
            style={{ animation: 'voiceNoteOverlayRise 360ms cubic-bezier(0.2, 0.7, 0.3, 1)' }}
          >
            <p className="text-white/80 text-base font-light italic tracking-wide">
              In their voice
            </p>
            <button
              onClick={togglePlay}
              className="mt-20 text-white/75 hover:text-white text-[15px] font-light tracking-wide flex items-center gap-3 transition-colors px-9 py-4 rounded-full hover:border-white/30"
              style={{ borderWidth: '1px', borderStyle: 'solid', borderColor: 'rgba(255,255,255,0.14)' }}
              data-testid="voice-note-overlay-play"
            >
              {playing ? (
                <Pause className="h-[14px] w-[14px]" strokeWidth={1.25} />
              ) : (
                <Play className="h-[14px] w-[14px] ml-0.5" strokeWidth={1.25} />
              )}
              <span>{playing ? 'Playing' : 'Play'}</span>
            </button>
            <p className="text-white/55 text-[13px] font-light italic mt-20 tracking-wide">
              For you, when you&rsquo;re ready
            </p>
          </div>
          <style>{`
            @keyframes voiceNoteOverlayFade { from { opacity: 0; } to { opacity: 1; } }
            @keyframes voiceNoteOverlayRise {
              0% { opacity: 0; transform: translateY(8px); }
              100% { opacity: 1; transform: translateY(0); }
            }
          `}</style>
        </div>,
        document.body
      )}

      <audio
        ref={audioRef}
        src={voiceUrl}
        onEnded={() => setPlaying(false)}
        preload="metadata"
      />
    </>
  );
}

// Shared waveform animation
function WaveformBars({ color }) {
  return (
    <>
      <div className="flex gap-[2px] items-end h-3">
        {[1, 2, 3, 4, 3].map((h, i) => (
          <div key={i} className="w-[2px] rounded-full" style={{ height: `${h * 3}px`, backgroundColor: color, opacity: 0.5, animation: `voiceWave 0.8s ease-in-out ${i * 0.1}s infinite alternate` }} />
        ))}
      </div>
      <style>{`@keyframes voiceWave { from { transform: scaleY(0.4); } to { transform: scaleY(1); } }`}</style>
    </>
  );
}


function GuestSearchContent() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [event, setEvent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [isReturning, setIsReturning] = useState(false);
  const [showNotYou, setShowNotYou] = useState(false);
  // Guest Lens — photo moments
  const [photoPhase, setPhotoPhase] = useState('idle'); // 'idle' | 'preview' | 'uploading' | 'counter' | 'closing' | 'limit'
  const [photoFile, setPhotoFile] = useState(null);
  const [photoCapturedAt, setPhotoCapturedAt] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoNote, setPhotoNote] = useState('');
  const [uploadCount, setUploadCount] = useState(0);
  const fileInputRef = useRef(null);
  // Share-moment hint: animate-fade-in once per session (subsequent visits show static)
  const [shareHintAnimateNow, setShareHintAnimateNow] = useState(false);
  // ─── In your voice — guest voice note ────────────────────
  // Modal phases: 'idle' (modal open, "Tap to speak") | 'recording' | 'review' (3 buttons) |
  // 'sending' | 'sent' | 'cue' (sub-state during recording around ~50s for "A breath left")
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voicePhase, setVoicePhase] = useState('idle');
  const [voiceCue, setVoiceCue] = useState(false); // "A breath left" cue
  const [voiceExisting, setVoiceExisting] = useState(null); // {audio_url, duration_sec, ...}
  const [voicePlayOpen, setVoicePlayOpen] = useState(false);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordedBlobRef = useRef(null);
  const recordedDurationRef = useRef(0);
  const recordStartRef = useRef(0);
  const cueTimerRef = useRef(null);
  const autoStopTimerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showTableGuests, setShowTableGuests] = useState(false);
  const [tableGuests, setTableGuests] = useState([]);
  const [loadingTableGuests, setLoadingTableGuests] = useState(false);
  const [showFloorPlanPage, setShowFloorPlanPage] = useState(false);
  const [theme, setTheme] = useState(null);
  const [tables, setTables] = useState([]);
  const [venueElements, setVenueElements] = useState([]);
  const [customPieces, setCustomPieces] = useState([]);
  const [textBoxes, setTextBoxes] = useState([]);
  const { t, setLanguage, language } = useLanguage();
  
  // Broadcast state
  const [broadcasts, setBroadcasts] = useState([]);
  const [activeBroadcast, setActiveBroadcast] = useState(null);
  const [missedBroadcasts, setMissedBroadcasts] = useState([]);
  const [showMissedBanner, setShowMissedBanner] = useState(false);
  const lastBroadcastTime = useRef(null);
  const seenBroadcasts = useRef(new Set());
  const pageVisibleRef = useRef(true);
  
  // Push notification state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);
  const [showPushOverlay, setShowPushOverlay] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [requestingPermission, setRequestingPermission] = useState(false);
  const pushSetupAttempted = useRef(false);

  // Plan viewport measurement (for floor plan sizing)
  const planViewportRef = useRef(null);
  const [planViewportSize, setPlanViewportSize] = useState({ width: 0, height: 0 });
  const FOOTER_H = 56; // px (safe for text + iPhone safe area)

  // Measure plan viewport with ResizeObserver
  React.useLayoutEffect(() => {
    const el = planViewportRef.current;
    if (!el) return;

    const update = () => {
      const r = el.getBoundingClientRect();
      if (r.width && r.height) setPlanViewportSize({ width: r.width, height: r.height });
    };

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, [showFloorPlanPage]);

  useEffect(() => {
    fetchEvent();
    registerServiceWorker();
  }, [slug]);

  // ─── Soft Recognition: auto-load guest from URL param or localStorage ───
  useEffect(() => {
    if (!event || selectedGuest) return;
    const gParam = searchParams.get('g');
    let guestName = null;

    // 1. Check URL param
    if (gParam) {
      guestName = gParam.replace(/-/g, ' ');
    } else {
      // 2. Fallback to localStorage
      try {
        const stored = JSON.parse(localStorage.getItem(`sitoa_guest_${slug}`) || 'null');
        if (stored?.full_name) guestName = stored.full_name;
      } catch { /* */ }
    }

    if (!guestName) return;

    // Auto-lookup this guest
    const autoLookup = async () => {
      try {
        const res = await axios.get(`${API}/public/event/${slug}/search`, { params: { q: guestName } });
        const match = res.data.find(g => g.full_name.toLowerCase() === guestName.toLowerCase());
        if (match) {
          setSelectedGuest(match);
          setSearchQuery(match.full_name);
          setIsReturning(true);
          setShowNotYou(true);
          // Update localStorage on this device
          const guestSlug = match.full_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
          try {
            localStorage.setItem(`sitoa_guest_${slug}`, JSON.stringify({ full_name: match.full_name, slug: guestSlug, ts: Date.now() }));
          } catch { /* */ }
          // Update URL if not already set
          if (!gParam) {
            setSearchParams({ g: guestSlug }, { replace: true });
          }
          // Fire-and-forget engagement beacon (admin metrics)
          try {
            fetch(`${API}/public/event/${slug}/guest-seen`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ guest_name: match.full_name }),
              keepalive: true,
            }).catch(() => {});
          } catch { /* */ }
          // Fade "Not you?" after 5 seconds
          setTimeout(() => setShowNotYou(false), 10000);
        }
      } catch { /* search failed, show normal flow */ }
    };
    autoLookup();
  }, [event]); // eslint-disable-line react-hooks/exhaustive-deps

  
  // Push notification - event ID from push-status API
  const [pushEventId, setPushEventId] = useState(null);
  
  // Fetch push status and show appropriate overlay when event loads
  useEffect(() => {
    if (!event) return;
    
    const fetchPushStatus = async () => {
      try {
        const response = await axios.get(`${API}/public/event/${slug}/push-status`);
        const { event_id, push_enabled, vapid_public_key } = response.data;
        
        setPushEventId(event_id);
        setPushEnabled(push_enabled);
        setVapidKey(vapid_public_key);
        
        // DISABLED: Push notifications overlay - needs testing and improvement
        // Show overlay if push is enabled
        /*
        if (push_enabled && vapid_public_key) {
          setTimeout(() => {
            // Check if iOS (any browser) and NOT running as PWA
            if (isIOS() && !isStandalonePWA()) {
              // Show iOS guide to add to Home Screen
              setShowIOSGuide(true);
            } else {
              // Android/Desktop or iOS PWA - show bell directly
              setShowPushOverlay(true);
            }
          }, 500);
        }
        */
      } catch (error) {
        console.error('Failed to fetch push status:', error);
      }
    };
    
    fetchPushStatus();
  }, [event, slug]);
  
  // Handle enabling push notifications (triggered by user tap on overlay)
  const handleEnablePush = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Prevent double-tap
    if (requestingPermission) return;
    setRequestingPermission(true);
    
    try {
      // Check if push is supported
      if (!isPushSupported()) {
        toast.error('Push notifications are not supported. Make sure you opened this from your Home Screen, not Safari.');
        setShowPushOverlay(false);
        setRequestingPermission(false);
        return;
      }
      
      // Request permission from browser
      const permission = await requestNotificationPermission();
      
      if (permission === 'granted') {
        if (vapidKey && pushEventId) {
          try {
            await subscribeToPush(pushEventId, vapidKey);
            setPushSubscribed(true);
            toast.success('Notifications enabled! You will receive announcements.');
          } catch (err) {
            console.error('Subscribe error:', err);
            toast.error('Failed to subscribe: ' + err.message);
          }
        } else {
          toast.error('Missing event data. Please refresh and try again.');
        }
      } else if (permission === 'denied') {
        toast.error('Notifications were blocked. Please enable them in Settings.');
      } else {
        toast.info('Notification permission: ' + permission);
      }
    } catch (error) {
      console.error('Push error:', error);
      toast.error('Error: ' + error.message);
    }
    
    // Always close overlay after interaction
    setShowPushOverlay(false);
    setRequestingPermission(false);
  };
  
  // Skip/dismiss the push notification overlay
  const handleSkipPush = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setShowPushOverlay(false);
  };
  
  // Handle page visibility change (for missed messages)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        pageVisibleRef.current = true;
        // Check for missed messages when page becomes visible
        if (missedBroadcasts.length > 0) {
          setShowMissedBanner(true);
        }
      } else {
        pageVisibleRef.current = false;
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [missedBroadcasts]);

  // Poll for broadcasts
  useEffect(() => {
    if (!event) return;
    
    const fetchBroadcasts = async () => {
      try {
        const params = lastBroadcastTime.current ? { since: lastBroadcastTime.current } : {};
        const response = await axios.get(`${API}/public/event/${slug}/broadcasts`, { params });
        
        if (response.data.length > 0) {
          // Find new broadcasts we haven't seen
          const newBroadcasts = response.data.filter(b => !seenBroadcasts.current.has(b.id));
          
          if (newBroadcasts.length > 0) {
            // Show the most recent new broadcast
            const latestBroadcast = newBroadcasts[0];
            seenBroadcasts.current.add(latestBroadcast.id);
            
            // If page is not visible, add to missed broadcasts
            if (!pageVisibleRef.current) {
              setMissedBroadcasts(prev => [latestBroadcast, ...prev]);
            } else {
              // Play sound/vibrate if requested and page is visible
              if (latestBroadcast.play_sound) {
                playNotificationSound();
              }
              if (latestBroadcast.vibrate) {
                vibrateDevice();
              }
              
              setActiveBroadcast(latestBroadcast);
              
              // Auto-dismiss normal messages after 5 seconds
              if (latestBroadcast.message_type === 'normal') {
                setTimeout(() => {
                  setActiveBroadcast(current => 
                    current?.id === latestBroadcast.id ? null : current
                  );
                }, 5000);
              }
            }
          }
          
          // Update last broadcast time
          lastBroadcastTime.current = response.data[0].created_at;
        }
      } catch (error) {
        console.error('Failed to fetch broadcasts');
      }
    };
    
    // Initial fetch
    fetchBroadcasts();
    
    // Poll every 3 seconds
    const interval = setInterval(fetchBroadcasts, 3000);
    
    return () => clearInterval(interval);
  }, [event, slug]);

  const dismissBroadcast = useCallback(() => {
    setActiveBroadcast(null);
  }, []);

  // Sync language with event's language setting and load theme
  useEffect(() => {
    if (event) {
      if (event.language) {
        setLanguage(event.language);
      }
      // Load color theme - check for customization mode
      if (event.theme_mode === 'customization' && event.custom_colors) {
        const customTheme = buildCustomTheme(event.custom_colors);
        setTheme(customTheme);
      } else {
        const eventTheme = getTheme(event.color_theme || 'coastalSilk');
        setTheme(eventTheme);
      }
    }
  }, [event, setLanguage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2 && !showTableGuests) {
        searchGuests();
      } else if (!showTableGuests) {
        setResults([]);
        setNotFound(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, showTableGuests]);

  const fetchEvent = async () => {
    try {
      const [eventRes, tablesRes, venueElementsRes, customPiecesRes, textBoxesRes] = await Promise.all([
        axios.get(`${API}/public/event/${slug}`),
        axios.get(`${API}/public/event/${slug}/floor-plan`),
        axios.get(`${API}/public/event/${slug}/venue-elements`),
        axios.get(`${API}/public/event/${slug}/custom-pieces`),
        axios.get(`${API}/public/event/${slug}/text-boxes`).catch(() => ({ data: [] }))
      ]);
      setEvent(eventRes.data);
      setTables(tablesRes.data || []);
      setVenueElements(venueElementsRes.data || []);
      setCustomPieces(customPiecesRes.data || []);
      setTextBoxes(textBoxesRes.data || []);
    } catch (error) {
      console.error('Event not found');
    } finally {
      setLoading(false);
    }
  };

  const searchGuests = async () => {
    setSearching(true);
    try {
      const response = await axios.get(`${API}/public/event/${slug}/search`, {
        params: { q: searchQuery }
      });
      setResults(response.data);
      setNotFound(response.data.length === 0);
    } catch (error) {
      console.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Sync uploadCount with the backend whenever we have a confirmed selectedGuest
  useEffect(() => {
    if (!selectedGuest?.full_name || !slug) return;
    let cancelled = false;
    // Reset stale state from any previously-selected guest BEFORE fetching
    setUploadCount(0);
    setPhotoPhase('idle');
    (async () => {
      try {
        const res = await axios.get(`${API}/public/event/${slug}/guest-moments-count`, {
          params: { guest_name: selectedGuest.full_name },
        });
        if (cancelled) return;
        const srvCount = Math.min(3, Math.max(0, Number(res.data?.count || 0)));
        setUploadCount(srvCount);
        // If they've already hit the cap, enter limit phase silently (no closing message on page reload)
        if (srvCount >= 3) {
          setPhotoPhase('limit');
        } else {
          setPhotoPhase('idle');
        }
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [selectedGuest?.full_name, slug]);

  // Share-moment hint: fade-in animation only on first encounter this session
  useEffect(() => {
    if (!selectedGuest?.full_name || !slug || !isReturning) return;
    const key = `sitoa_share_hint_seen_${slug}`;
    let alreadySeen = false;
    try { alreadySeen = sessionStorage.getItem(key) === '1'; } catch { /* */ }
    if (!alreadySeen) {
      setShareHintAnimateNow(true);
      try { sessionStorage.setItem(key, '1'); } catch { /* */ }
    } else {
      setShareHintAnimateNow(false);
    }
  }, [selectedGuest?.full_name, slug, isReturning]);

  const handleSelectGuest = (guest) => {
    setSelectedGuest(guest);
    setResults([]);
    setSearchQuery(guest.full_name);
    // Personalize URL
    const guestSlug = guest.full_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setSearchParams({ g: guestSlug }, { replace: true });
    // Store in localStorage for fast re-entry
    try {
      localStorage.setItem(`sitoa_guest_${slug}`, JSON.stringify({
        full_name: guest.full_name,
        slug: guestSlug,
        ts: Date.now(),
      }));
    } catch { /* storage full or private mode */ }
    // Fire-and-forget engagement beacon (admin metrics)
    try {
      fetch(`${API}/public/event/${slug}/guest-seen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guest_name: guest.full_name }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* */ }
  };

  const handleNotYou = () => {
    try { localStorage.removeItem(`sitoa_guest_${slug}`); } catch { /* */ }
    setSelectedGuest(null);
    setSearchQuery('');
    setIsReturning(false);
    setUploadCount(0);
    setPhotoPhase('idle');
    setShowNotYou(false);
    setSearchParams({}, { replace: true });
  };

  const handleShareSaveForLater = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Sitoa', url });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied!');
      } catch { /* */ }
    }
  };

  // ─── In your voice — guest voice note ──────────────────────────
  // Fetch existing voice note (if any) for the locked "In your voice" final state
  useEffect(() => {
    if (!selectedGuest?.full_name || !slug) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/public/event/${slug}/guest-voice-note`, {
          params: { guest_name: selectedGuest.full_name },
        });
        if (!cancelled) setVoiceExisting(res.data?.voice_note || null);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [selectedGuest?.full_name, slug]);

  const cleanupRecorder = () => {
    try { mediaRecorderRef.current?.stop(); } catch { /* */ }
    try {
      const stream = mediaRecorderRef.current?.stream;
      stream?.getTracks().forEach(t => t.stop());
    } catch { /* */ }
    mediaRecorderRef.current = null;
    if (cueTimerRef.current) { clearTimeout(cueTimerRef.current); cueTimerRef.current = null; }
    if (autoStopTimerRef.current) { clearTimeout(autoStopTimerRef.current); autoStopTimerRef.current = null; }
  };

  const handleOpenVoiceModal = () => {
    setVoicePhase('idle');
    setVoiceCue(false);
    recordedChunksRef.current = [];
    recordedBlobRef.current = null;
    recordedDurationRef.current = 0;
    setVoiceOpen(true);
  };

  const handleCloseVoiceModal = () => {
    cleanupRecorder();
    setVoiceOpen(false);
    setVoicePhase('idle');
    setVoiceCue(false);
  };

  const handleStartRecording = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      // No mic API at all — silent fallback
      setVoicePhase('mic-denied');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      recordedChunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mr.mimeType || 'audio/webm' });
        recordedBlobRef.current = blob;
        const elapsed = Math.round((Date.now() - recordStartRef.current) / 1000);
        recordedDurationRef.current = elapsed;
        // Silently discard sub-1s recordings — don't punish a hesitant tap
        if (blob.size < 1024 || elapsed < 1) {
          recordedBlobRef.current = null;
          recordedDurationRef.current = 0;
          setVoicePhase('idle');
          return;
        }
        setVoicePhase('review');
      };
      mediaRecorderRef.current = mr;
      recordStartRef.current = Date.now();
      mr.start();
      setVoicePhase('recording');
      // Soft cue ~50s in
      cueTimerRef.current = setTimeout(() => {
        setVoiceCue(true);
        setTimeout(() => setVoiceCue(false), 2400);
      }, 50000);
      // Hard auto-stop at 60s
      autoStopTimerRef.current = setTimeout(() => {
        try { mr.stop(); } catch { /* */ }
      }, 60000);
    } catch {
      setVoicePhase('mic-denied');
    }
  };

  const handleStopRecording = () => {
    if (cueTimerRef.current) { clearTimeout(cueTimerRef.current); cueTimerRef.current = null; }
    if (autoStopTimerRef.current) { clearTimeout(autoStopTimerRef.current); autoStopTimerRef.current = null; }
    try { mediaRecorderRef.current?.stop(); } catch { /* */ }
    // Stream tracks released after stop event
    setTimeout(() => {
      try {
        const stream = mediaRecorderRef.current?.stream;
        stream?.getTracks().forEach(t => t.stop());
      } catch { /* */ }
    }, 50);
  };

  const handleReRecord = () => {
    recordedChunksRef.current = [];
    recordedBlobRef.current = null;
    recordedDurationRef.current = 0;
    setVoiceCue(false);
    setVoicePhase('idle');
  };

  const handleSendVoice = async () => {
    const blob = recordedBlobRef.current;
    if (!blob) return;
    setVoicePhase('sending');
    const fd = new FormData();
    fd.append('audio', blob, 'voice.webm');
    fd.append('guest_name', selectedGuest?.full_name || '');
    fd.append('duration_sec', String(recordedDurationRef.current || 0));
    try {
      const res = await axios.post(`${API}/public/event/${slug}/voice-note`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.voice_note) setVoiceExisting(res.data.voice_note);
      setVoicePhase('sent');
      // Fade out & close after a beat
      setTimeout(() => {
        setVoiceOpen(false);
        setVoicePhase('idle');
      }, 1400);
    } catch {
      // Silent recovery — return to review state, user can re-tap
      setVoicePhase('review');
    }
  };

  // Cleanup recorder if user navigates away
  useEffect(() => () => cleanupRecorder(), []);


  // ─── Guest Lens: Photo Moments ─────────────────────────────────
  const handleShareMoment = () => {
    if (uploadCount >= 3) {
      setPhotoPhase('limit');
      return;
    }
    fileInputRef.current?.click();
  };

  // Read EXIF DateTimeOriginal from a JPEG ArrayBuffer. Returns ISO string or null.
  // Minimal parser: we only read TAG 0x9003 (DateTimeOriginal). Silent fallback on anything unexpected.
  const readExifCapturedAt = (arrayBuffer) => {
    try {
      const view = new DataView(arrayBuffer);
      if (view.getUint16(0, false) !== 0xFFD8) return null; // not JPEG
      const len = view.byteLength;
      let offset = 2;
      while (offset < len) {
        if (view.getUint8(offset) !== 0xFF) return null;
        const marker = view.getUint8(offset + 1);
        if (marker === 0xE1) {
          // APP1 (EXIF)
          const app1Len = view.getUint16(offset + 2, false);
          const tiffOffset = offset + 10; // skip APP1 len (2) + "Exif\0\0" (6)
          if (view.getUint32(offset + 4, false) !== 0x45786966) return null; // "Exif"
          const little = view.getUint16(tiffOffset, false) === 0x4949;
          const ifd0Offset = tiffOffset + view.getUint32(tiffOffset + 4, little);
          const ifd0Entries = view.getUint16(ifd0Offset, little);
          let exifIFDOffset = null;
          for (let i = 0; i < ifd0Entries; i++) {
            const entry = ifd0Offset + 2 + i * 12;
            const tag = view.getUint16(entry, little);
            if (tag === 0x8769) {
              exifIFDOffset = tiffOffset + view.getUint32(entry + 8, little);
              break;
            }
          }
          if (exifIFDOffset === null) return null;
          const exifEntries = view.getUint16(exifIFDOffset, little);
          for (let i = 0; i < exifEntries; i++) {
            const entry = exifIFDOffset + 2 + i * 12;
            const tag = view.getUint16(entry, little);
            if (tag === 0x9003) {
              // DateTimeOriginal — ASCII string at offset (value > 4 bytes)
              const count = view.getUint32(entry + 4, little);
              const valOffset = tiffOffset + view.getUint32(entry + 8, little);
              let s = '';
              for (let j = 0; j < count - 1 && valOffset + j < len; j++) {
                s += String.fromCharCode(view.getUint8(valOffset + j));
              }
              // EXIF format: "YYYY:MM:DD HH:MM:SS"
              const m = s.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
              if (!m) return null;
              // Treat as local time (EXIF is ambiguous; convert to UTC-naive ISO)
              const iso = `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`;
              return iso;
            }
          }
          return null;
        } else if ((marker & 0xFF00) !== 0xFF00) {
          return null;
        } else {
          const segLen = view.getUint16(offset + 2, false);
          offset += 2 + segLen;
        }
      }
      return null;
    } catch { return null; }
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // First, try to read EXIF DateTimeOriginal (original capture time)
    const exifReader = new FileReader();
    let capturedAt = null;
    exifReader.onload = (evExif) => {
      capturedAt = readExifCapturedAt(evExif.target.result);
      setPhotoCapturedAt(capturedAt);
    };
    try { exifReader.readAsArrayBuffer(file); } catch { /* noop */ }

    // Then compress via canvas as before
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (ev) => {
      img.onload = () => {
        const MAX = 1600;
        let w = img.width, h = img.height;
        if (w > MAX || h > MAX) {
          if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
          else { w = Math.round(w * MAX / h); h = MAX; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        canvas.toBlob((blob) => {
          setPhotoFile(blob);
          setPhotoPreview(URL.createObjectURL(blob));
          setPhotoNote('');
          setPhotoPhase('preview');
        }, 'image/jpeg', 0.85);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUsePhoto = async () => {
    if (!photoFile || !selectedGuest) return;
    setPhotoPhase('uploading');
    try {
      const formData = new FormData();
      formData.append('photo', photoFile, `moment_${Date.now()}.jpg`);
      formData.append('note', photoNote);
      formData.append('guest_name', selectedGuest.full_name);
      if (photoCapturedAt) formData.append('captured_at', photoCapturedAt);
      await axios.post(`${API}/public/event/${slug}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Subtle haptic (Android + some browsers); silently ignored elsewhere
      try { if (navigator.vibrate) navigator.vibrate(10); } catch { /* */ }

      const newCount = uploadCount + 1;
      setUploadCount(newCount);
      setPhotoFile(null);
      setPhotoCapturedAt(null);
      setPhotoPreview(null);
      setPhotoNote('');

      if (newCount >= 3) {
        // 3rd photo — show counter first, then breath, then closing message
        setPhotoPhase('counter');
        setTimeout(() => {
          setPhotoPhase('idle');
          setTimeout(() => {
            setPhotoPhase('closing');
            setTimeout(() => setPhotoPhase('limit'), 4500);
          }, 300);
        }, 2000);
      } else {
        // 1st or 2nd — ephemeral floating counter
        setPhotoPhase('counter');
        setTimeout(() => setPhotoPhase('idle'), 2000);
      }
    } catch {
      setPhotoPhase('preview'); // back to preview on error
    }
  };

  const handleRetake = () => {
    setPhotoFile(null);
    setPhotoCapturedAt(null);
    setPhotoPreview(null);
    setPhotoNote('');
    setPhotoPhase('idle');
  };


  const handleTableClick = async () => {
    if (!selectedGuest?.table_name) return;
    
    setLoadingTableGuests(true);
    try {
      const response = await axios.get(`${API}/public/event/${slug}/table/${encodeURIComponent(selectedGuest.table_name)}/guests`);
      // Keep original order from backend, but move highlighted guest to 3rd position
      const guests = [...response.data];
      const highlightedIndex = guests.findIndex(g => g.full_name === selectedGuest.full_name);
      
      if (highlightedIndex > 2) {
        // Remove highlighted guest from current position and insert at position 2 (3rd place)
        const [highlightedGuest] = guests.splice(highlightedIndex, 1);
        guests.splice(2, 0, highlightedGuest);
      } else if (highlightedIndex >= 0 && highlightedIndex < 2) {
        // If highlighted guest is in 1st or 2nd position, move them to 3rd position
        if (guests.length > 2) {
          const [highlightedGuest] = guests.splice(highlightedIndex, 1);
          guests.splice(2, 0, highlightedGuest);
        }
      }
      // If highlightedIndex === 2, guest is already in 3rd position, no change needed
      
      setTableGuests(guests);
      setShowTableGuests(true);
    } catch (error) {
      console.error('Failed to load table guests');
    } finally {
      setLoadingTableGuests(false);
    }
  };

  const handleBackToSearch = () => {
    setShowTableGuests(false);
    setTableGuests([]);
  };

  // Get theme colors with fallback
  const colors = theme?.colors || getTheme('coastalSilk').colors;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="animate-pulse font-heading text-xl" style={{ color: colors.primary }}>Loading...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <h1 className="font-heading text-3xl mb-4" style={{ color: colors.primary }}>Event Not Found</h1>
          <p style={{ color: colors.textSecondary }}>This event doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  // Determine background image
  const backgroundImageUrl = event?.background_image_url 
    ? `${process.env.REACT_APP_BACKEND_URL}${event.background_image_url}`
    : 'https://images.unsplash.com/photo-1543094152-7f8122e97b66?crop=entropy&cs=srgb&fm=jpg&q=85';
  
  const overlayIntensity = event?.overlay_intensity || 50;

  // Floor Plan Full Screen View
  if (showFloorPlanPage) {
    return (
      <div className="fixed inset-0 z-50 h-[100dvh] flex flex-col min-h-0" style={{ backgroundColor: colors.background }}>
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${backgroundImageUrl})`,
            opacity: event?.background_image_url ? 1 : 0.15
          }}
        />
        <div 
          className="absolute inset-0"
          style={{ 
            background: event?.background_image_url 
              ? `rgba(0, 0, 0, ${Math.min(overlayIntensity + 20, 90) / 100})`
              : `${colors.background}ee`
          }}
        />
        
        {/* Floating Close Button - Top Left */}
        <button
          onClick={() => setShowFloorPlanPage(false)}
          className="absolute top-3 left-3 z-20 w-10 h-10 flex items-center justify-center rounded-full transition-all hover:scale-110 backdrop-blur-md"
          style={{ 
            backgroundColor: `${colors.cardBackground}cc`,
            color: colors.text,
            border: `1px solid ${colors.border || colors.primary}40`
          }}
          data-testid="floor-plan-back-btn"
        >
          <X className="h-5 w-5" />
        </button>
        
        {/* Floating Table Name Badge - Top Right */}
        <div 
          className="absolute top-3 right-3 z-20 flex items-center gap-2 font-mono text-sm font-bold px-4 py-2.5 rounded-xl backdrop-blur-md"
          style={{ 
            backgroundColor: colors.tableButton, 
            color: colors.tableButtonText,
            boxShadow: `0 4px 15px ${colors.tableButton}40`
          }}
        >
          <Users className="h-4 w-4" />
          {selectedGuest?.table_name?.toLowerCase().includes('table') 
            ? selectedGuest?.table_name 
            : `Table ${selectedGuest?.table_name}`}
        </div>
        
        {/* Full Screen Floor Plan Box */}
        <div className="relative flex-1 flex flex-col pt-12">
          <div className="relative flex-1 flex flex-col min-h-0">
            {/* Floor Plan Header - minimal */}
            <div 
              className="px-2 py-1 flex items-center gap-1 shrink-0"
              style={{ borderBottom: `1px solid ${colors.primary}22` }}
            >
              <MapPin className="h-3 w-3" style={{ color: colors.primary }} />
              <span className="text-[13px]" style={{ color: colors.primary }}>Floor Plan</span>
            </div>
            
            {/* Plan viewport - ONLY this hides overflow */}
            <div
              ref={planViewportRef}
              className="flex-1 min-h-0 overflow-hidden"
              style={{ paddingBottom: `${FOOTER_H}px` }}
            >
              <PublicFloorPlan
                tables={tables}
                venueElements={venueElements}
                customPieces={customPieces}
                textBoxes={textBoxes}
                highlightedTableId={selectedGuest?.table_id}
                themeColors={{
                  primary: colors.primary,
                  primaryHover: colors.primaryHover,
                  background: colors.background,
                  cardBackground: colors.cardBackground,
                  accent: colors.accent,
                  border: colors.border,
                  text: colors.text,
                  textSecondary: colors.textSecondary,
                  textMuted: colors.textMuted,
                }}
                floorPlanStyle={event?.floor_plan_style}
                fullScreen={true}
                containerSize={{
                  width: planViewportSize.width,
                  height: Math.max(0, planViewportSize.height - FOOTER_H),
                }}
              />
            </div>
            
            {/* Footer (fixed overlay above plan) */}
            <div
              className="fixed left-0 right-0 text-center"
              style={{
                bottom: 0,
                zIndex: 999999,
                paddingTop: 6,
                paddingBottom: "calc(env(safe-area-inset-bottom) + 8px)",
                background: colors.background,
              }}
            >
              <span className="text-[13px]" style={{ color: colors.textMuted }}>
                {language === 'pt' ? 'Sua mesa está destacada acima' :
                 language === 'fr' ? 'Votre table est mise en évidence ci-dessus' :
                 language === 'es' ? 'Su mesa está resaltada arriba' :
                 language === 'ru' ? 'Ваш стол выделен выше' :
                 'Your table is highlighted above'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: colors.background }}>
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{ 
          backgroundImage: `url(${backgroundImageUrl})`,
          opacity: event?.background_image_url ? 1 : 0.2 // Full opacity for custom images
        }}
      />
      {/* Overlay for text readability */}
      <div 
        className="absolute inset-0"
        style={{ 
          background: event?.background_image_url 
            ? `rgba(0, 0, 0, ${overlayIntensity / 100})`
            : `linear-gradient(to top, ${colors.background}, ${colors.background}cc, ${colors.background}99)` 
        }}
      />
      
      {/* Push Notification Permission Overlay - For Android/Desktop/iOS PWA */}
      {showPushOverlay && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.6)' }}
          data-testid="push-overlay"
        >
          <div className="text-center">
            {/* Bell Icon Button */}
            <button
              onClick={handleEnablePush}
              disabled={requestingPermission}
              className="w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95 shadow-xl"
              style={{ 
                backgroundColor: colors.secondary || '#8B7355',
                boxShadow: `0 8px 32px ${colors.secondary || '#8B7355'}50`
              }}
              data-testid="push-overlay-bell"
            >
              {requestingPermission ? (
                <div className="animate-spin w-10 h-10 border-4 border-white border-t-transparent rounded-full" />
              ) : (
                <BellRing className="w-12 h-12 text-white" />
              )}
            </button>
            
            {/* Text */}
            <p className="text-base font-medium mb-4" style={{ color: colors.text || '#333' }}>
              Tap to enable notifications
            </p>
            
            {/* Skip button */}
            <button
              onClick={handleSkipPush}
              className="text-sm underline transition-colors"
              style={{ color: colors.textMuted || '#888' }}
              data-testid="push-overlay-skip"
            >
              Skip
            </button>
          </div>
        </div>
      )}
      
      {/* iOS Safari Guide - Add to Home Screen */}
      {showIOSGuide && (
        <div 
          className="fixed inset-0 z-50 flex flex-col p-4"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.92)' }}
          data-testid="ios-guide-overlay"
        >
          {/* Arrow pointing to top-right share button */}
          <div className="flex justify-end pr-4 pt-2 animate-bounce">
            <div className="flex flex-col items-center">
              <span className="text-xs font-medium mb-1" style={{ color: colors.secondary || '#8B7355' }}>
                Tap here
              </span>
              <svg 
                className="w-8 h-8" 
                fill={colors.secondary || '#8B7355'} 
                viewBox="0 0 24 24"
              >
                <path d="M12 8l6 6H6l6-6z"/>
              </svg>
            </div>
          </div>
          
          {/* Main content - centered */}
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-xs">
              {/* Bell Icon */}
              <div 
                className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                style={{ backgroundColor: colors.secondary || '#8B7355' }}
              >
                <BellRing className="w-8 h-8 text-white" />
              </div>
              
              {/* Title */}
              <h3 className="text-lg font-bold mb-2" style={{ color: colors.text || '#333' }}>
                Enable Notifications
              </h3>
              
              <p className="text-sm mb-4" style={{ color: colors.textMuted || '#666' }}>
                To receive event announcements:
              </p>
              
              {/* Simple Steps */}
              <div className="text-left space-y-3 mb-4 bg-white/50 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <span className="text-lg">1.</span>
                  <div className="flex items-center gap-2">
                    <span style={{ color: colors.text || '#333' }}>Tap</span>
                    {/* Share icon illustration */}
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke={colors.secondary || '#8B7355'} strokeWidth="2">
                      <rect x="4" y="8" width="16" height="14" rx="2" />
                      <path d="M12 2v12M8 6l4-4 4 4" />
                    </svg>
                    <span style={{ color: colors.text || '#333' }}>at top right</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">2.</span>
                  <span style={{ color: colors.text || '#333' }}>
                    Tap <strong>⋯</strong> and select <strong>"Add to Home Screen"</strong>
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-lg">3.</span>
                  <span style={{ color: colors.text || '#333' }}>
                    Open from Home Screen and enable notifications
                  </span>
                </div>
              </div>
              
              {/* Skip button */}
              <button
                onClick={() => setShowIOSGuide(false)}
                className="text-sm underline"
                style={{ color: colors.textMuted || '#888' }}
                data-testid="ios-guide-skip"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Language Selector — compact glass globe (default compact, same 7 languages).
          Self-positions via portal to viewport top-right. */}
      <PublicLanguageSwitcher
        value={language}
        onValueChange={setLanguage}
        initialCompact={true}
        options={[
          { code: 'en' },
          { code: 'pt' },
          { code: 'fr' },
          { code: 'es' },
          { code: 'el' },
          { code: 'ru' },
          { code: 'it' },
        ]}
      />
      
      {/* Push Notification Permission Prompt */}
      {/* Missed Messages Banner */}
      {showMissedBanner && missedBroadcasts.length > 0 && (
        <div 
          className="fixed top-4 left-4 right-4 z-50 animate-in slide-in-from-top duration-300"
          data-testid="missed-messages-banner"
        >
          <div 
            className="max-w-lg mx-auto rounded-xl shadow-2xl backdrop-blur-lg overflow-hidden"
            style={{ 
              backgroundColor: `${colors.cardBackground || colors.background}f5`,
              border: `1px solid ${colors.secondary}40`
            }}
          >
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div 
                  className="flex-shrink-0 p-2 rounded-full"
                  style={{ backgroundColor: `${colors.secondary}20` }}
                >
                  <Bell className="h-5 w-5" style={{ color: colors.secondary }} />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium mb-2" style={{ color: colors.text }}>
                    You missed {missedBroadcasts.length} message{missedBroadcasts.length > 1 ? 's' : ''}
                  </p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {missedBroadcasts.map((b, i) => (
                      <p key={b.id} className="text-sm" style={{ color: colors.textSecondary }}>
                        {b.message_type === 'urgent' && <AlertTriangle className="h-3 w-3 inline mr-1 text-red-500" />}
                        {b.message}
                      </p>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowMissedBanner(false);
                    setMissedBroadcasts([]);
                  }}
                  className="flex-shrink-0 p-1"
                  style={{ color: colors.textSecondary }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Broadcast Notification Popup */}
      {activeBroadcast && (
        <div 
          className="fixed top-4 left-4 right-4 z-50 animate-in slide-in-from-top duration-300"
          data-testid="broadcast-popup"
        >
          <div 
            className="max-w-lg mx-auto rounded-xl shadow-2xl backdrop-blur-lg overflow-hidden"
            style={{ 
              backgroundColor: activeBroadcast.message_type === 'urgent' 
                ? 'rgba(239, 68, 68, 0.95)' 
                : `${colors.cardBackground || colors.background}f5`,
              border: activeBroadcast.message_type === 'urgent'
                ? '2px solid #dc2626'
                : `1px solid ${colors.border || colors.secondary}40`
            }}
          >
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div 
                  className="flex-shrink-0 p-2 rounded-full"
                  style={{ 
                    backgroundColor: activeBroadcast.message_type === 'urgent' 
                      ? 'rgba(255,255,255,0.2)' 
                      : `${colors.secondary}20`
                  }}
                >
                  {activeBroadcast.message_type === 'urgent' ? (
                    <AlertTriangle className="h-5 w-5 text-white" />
                  ) : (
                    <Bell className="h-5 w-5" style={{ color: colors.secondary }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p 
                    className="text-base font-medium"
                    style={{ 
                      color: activeBroadcast.message_type === 'urgent' 
                        ? 'white' 
                        : colors.text 
                    }}
                  >
                    {activeBroadcast.message}
                  </p>
                </div>
                <button
                  onClick={dismissBroadcast}
                  className="flex-shrink-0 p-1 rounded-full hover:opacity-80 transition-opacity"
                  style={{ 
                    color: activeBroadcast.message_type === 'urgent' 
                      ? 'white' 
                      : colors.textSecondary 
                  }}
                  data-testid="broadcast-dismiss-btn"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              {activeBroadcast.message_type === 'normal' && (
                <div className="mt-2 h-1 rounded-full overflow-hidden" style={{ backgroundColor: `${colors.secondary}30` }}>
                  <div 
                    className="h-full rounded-full animate-broadcast-progress"
                    style={{ backgroundColor: colors.secondary }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Content */}
      <div className="relative min-h-screen flex flex-col items-center justify-center p-4">
        {/* Event Header */}
        <div className="text-center mb-8 md:mb-12">
          <h1 
            className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-4" 
            style={{ color: colors.primary }}
            data-testid="event-name"
          >
            {event.name}
          </h1>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3" style={{ color: colors.textSecondary }}>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>{event.date ? format(new Date(event.date), 'PPP', { locale: dateLocales[language] || enUS }) : ''}</span>
            </div>
            <span className="hidden sm:inline">•</span>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{event.location}</span>
            </div>
          </div>
        </div>

        {/* Search Section or Table Guests View */}
        <div className="w-full max-w-lg">
          {!showTableGuests ? (
            <>
              <h2 className="text-center text-xl md:text-2xl mb-6 font-heading" style={{ color: colors.text }}>
                {t('findYourSeat')}
              </h2>
              
              {/* Search Input */}
              <div className="relative mb-4">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: colors.textMuted }} />
                <Input
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setSelectedGuest(null); }}
                  placeholder={t('searchPlaceholder')}
                  className="w-full pl-14 pr-6 py-6 text-lg rounded-xl"
                  style={{ 
                    backgroundColor: colors.inputBackground, 
                    borderColor: colors.border, 
                    color: colors.text 
                  }}
                  data-testid="guest-search-input"
                />
                {searching && (
                  <div className="absolute right-5 top-1/2 -translate-y-1/2">
                    <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: colors.primary, borderTopColor: 'transparent' }} />
                  </div>
                )}
              </div>

              {/* Search Results Dropdown */}
              {results.length > 0 && !selectedGuest && (
                <div 
                  className="backdrop-blur-lg rounded-xl overflow-hidden shadow-2xl mb-4" 
                  style={{ backgroundColor: colors.cardBackground, border: `1px solid ${colors.border}` }}
                  data-testid="search-results"
                >
                  {results.map((guest, index) => (
                    <button
                      key={index}
                      onClick={() => handleSelectGuest(guest)}
                      className="w-full px-5 py-4 text-left transition-colors"
                      style={{ borderBottom: `1px solid ${colors.border}` }}
                      data-testid={`search-result-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <User className="h-5 w-5" style={{ color: colors.primary }} />
                        <span style={{ color: colors.text }}>{guest.full_name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Not Found Message */}
              {notFound && searchQuery.length >= 2 && !selectedGuest && (
                <div 
                  className="backdrop-blur-lg rounded-xl p-6 text-center" 
                  style={{ backgroundColor: colors.cardBackground, border: `1px solid ${colors.border}` }}
                  data-testid="not-found-message"
                >
                  <p style={{ color: colors.textSecondary }}>{t('noResults')}</p>
                </div>
              )}

              {/* Selected Guest Card */}
              {selectedGuest && (
                <div 
                  className="backdrop-blur-xl rounded-2xl p-8 text-center" 
                  style={{ backgroundColor: colors.cardBackground, border: `1px solid ${colors.primary}40` }}
                  data-testid="guest-result-card"
                >
                  <div 
                    className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${colors.primary}20` }}
                  >
                    <User className="h-8 w-8" style={{ color: colors.primary }} />
                  </div>
                  <h3 className="font-heading text-2xl mb-4" style={{ color: colors.text }} data-testid="guest-name">
                    {selectedGuest.full_name}
                  </h3>
                  
                  {/* Event Message - Shown to all guests */}
                  {event?.event_message && (
                    <div 
                      className="mb-6 p-4 rounded-xl text-left"
                      style={{ backgroundColor: `${colors.primary}10`, border: `1px solid ${colors.primary}30` }}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl flex-shrink-0">💌</span>
                        <p className="text-sm whitespace-pre-wrap" style={{ color: colors.text }} data-testid="event-message">
                          {event.event_message}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Clickable Table Button */}
                  {selectedGuest.table_name ? (
                    <button
                      onClick={handleTableClick}
                      disabled={loadingTableGuests}
                      className="inline-block font-mono text-4xl font-bold px-8 py-4 rounded-xl transition-all duration-300 cursor-pointer hover:scale-105"
                      style={{ 
                        backgroundColor: colors.tableButton, 
                        color: colors.tableButtonText,
                        boxShadow: `0 0 20px ${colors.tableButton}40`
                      }}
                      data-testid="guest-table-btn"
                    >
                      {loadingTableGuests ? (
                        <div className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin mx-auto" style={{ borderColor: colors.tableButtonText, borderTopColor: 'transparent' }} />
                      ) : (
                        selectedGuest.table_name.toLowerCase().includes('table') 
                          ? selectedGuest.table_name 
                          : `Table ${selectedGuest.table_name}`
                      )}
                    </button>
                  ) : (
                    <div 
                      className="inline-block font-mono text-4xl font-bold px-8 py-4 rounded-xl"
                      style={{ backgroundColor: colors.border, color: colors.textMuted }}
                    >
                      -
                    </div>
                  )}
                  
                  {selectedGuest.table_name && (
                    <p className="text-xs mt-3" style={{ color: colors.textMuted }}>
                      {language === 'pt' ? 'Clique para ver os convidados da mesa' :
                       language === 'fr' ? 'Cliquez pour voir les invités de la table' :
                       language === 'es' ? 'Haz clic para ver los invitados de la mesa' :
                       language === 'ru' ? 'Нажмите, чтобы увидеть гостей за столом' :
                       'Tap to see table guests'}
                    </p>
                  )}
                  
                  {/* Table Message — text or voice */}
                  {(selectedGuest.table_message || selectedGuest.table_voice_note_url) && (
                    selectedGuest.table_voice_note_url ? (
                      <TableVoiceNotePlayer voiceUrl={`${process.env.REACT_APP_BACKEND_URL}${selectedGuest.table_voice_note_url}`} colors={colors} />
                    ) : (
                      <div 
                        className="mt-6 p-4 rounded-xl text-left"
                        style={{ backgroundColor: `${colors.accent}10`, border: `1px solid ${colors.accent}30` }}
                      >
                        <div className="flex items-start gap-3">
                          <svg 
                            className="w-6 h-5 flex-shrink-0 mt-0.5"
                            viewBox="0 0 61.85 50.81"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path 
                              fill="#999999"
                              d="M24.61,7.44c-.43,3.92-.92,7.84-1.36,11.77-.45,4.54-1.04,9.08-1.55,13.62-.56,5.17-1.16,10.33-1.76,15.5,7.72,0,15.43,0,23.15,0-.65-4.81-1.34-9.62-2.05-14.42-.71-5.36-1.54-10.7-2.28-16.05-.07-.4-.11-.8-.14-1.2l-.07-.11c-.33-3.04-.87-6.07-1.28-9.1-4.22,0-8.44,0-12.65,0ZM2.58,2.48c.1.73.22,1.41.6,2.05.12.17.19.35.42.38.56.08,1.13.03,1.69.04,16.97,0,33.94,0,50.92,0,.75,0,1.49.02,2.23-.06.48-.77.72-1.51.82-2.41-18.89,0-37.79,0-56.69,0M0,0h61.83c-.01,1.01.06,1.95-.12,2.93-.19,1.41-.65,2.93-1.8,3.86-.57.5-1.28.66-2.03.65-6.04,0-12.08,0-18.12,0,.31,2.08.59,4.16.88,6.24.47,3.53,1.03,7.05,1.52,10.58.65,4.94,1.43,9.86,2.1,14.8.52,3.92,1.14,7.83,1.67,11.75-9.6,0-19.19.02-28.79,0,.2-1.16.25-2.35.43-3.51.49-4.83,1.15-9.65,1.64-14.48.6-5.43,1.28-10.85,1.86-16.28.09-.55.11-1.12.2-1.67.25-2.48.54-4.95.83-7.42-5.83,0-11.66,0-17.49,0-.76,0-1.51.06-2.21-.3-.96-.51-1.56-1.46-1.91-2.47C-.01,3.14-.02,1.6,0,0"
                            />
                          </svg>
                          <div>
                            <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>{t('tableInfo')}:</p>
                            <p className="text-sm whitespace-pre-wrap" style={{ color: colors.text }} data-testid="table-message">
                              {selectedGuest.table_message}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                  
                  {/* Personal Guest Message — text or voice */}
                  {(selectedGuest.guest_message || selectedGuest.voice_note_url) && (
                    selectedGuest.voice_note_url ? (
                      <VoiceNotePlayer voiceUrl={`${process.env.REACT_APP_BACKEND_URL}${selectedGuest.voice_note_url}`} colors={colors} />
                    ) : (
                      <div 
                        className="mt-4 p-4 rounded-xl text-left"
                        style={{ backgroundColor: `${colors.primary}15`, border: `1px dashed ${colors.primary}50` }}
                      >
                        <div className="flex items-start gap-3">
                          <svg 
                            className="w-5 h-5 flex-shrink-0 mt-0.5"
                            viewBox="0 0 82.1 82.1"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path 
                              fill="#999999"
                              d="M73.6,0c2.6-.2,5.2,1.2,6.9,3.2,1.5,2,2.1,4.8,1.3,7.3-.4,1.6-1.4,2.9-2.5,4.1-10.8,10.8-21.6,21.6-32.3,32.3-1.4,1.3-2.4,3-4.5,3.3-2.4.3-4.8.7-7.2,1.1-1.6.3-3.4-.5-4.1-2-.6-1-.2-2.5,0-3.6.3-2,.6-4,.9-5.9.3-2,2.2-3.3,3.5-4.7,10.7-10.8,21.3-21.5,32.1-32.3,1.6-1.6,3.8-2.8,6.1-2.8h0Z"
                            />
                            <path 
                              fill="#999999"
                              d="M10.7,6.9c9,0,18.1,0,27.1,0,2.6,0,3.9,2.9,2.8,5-.7,1.1-1.8,1.8-3.1,1.8-8.9,0-17.9,0-26.8,0-1.4,0-2.5.6-3.3,1.6-.5.7-.5,1.4-.5,2.2v51.3c0,1.2,0,2.5.1,3.7.5,1.6,2,2.7,3.7,2.6,17.7,0,35.5,0,53.2,0,.8,0,1.6,0,2.4-.3,1.3-.6,2.2-1.9,2.1-3.4v-25c0-1.4-.2-2.6.6-3.8,1-1.9,4.2-2,5.5-.3.7.8.7,1.8.8,2.8,0,8.8,0,17.6,0,26.4,0,3.6-2.1,7.3-5.2,9.1-1.7,1.1-3.8,1.6-5.9,1.6-17.8,0-35.7,0-53.5,0-3,0-5.9-1.3-7.9-3.5-1.6-1.9-2.8-4.4-2.8-6.9,0-18.2,0-36.4,0-54.6,0-3.7,2.2-7.1,5.3-9,1.6-.9,3.5-1.4,5.4-1.3Z"
                            />
                          </svg>
                          <div>
                            <p className="text-xs font-medium mb-1" style={{ color: colors.textMuted }}>{t('personalNote')}:</p>
                            <p className="text-sm whitespace-pre-wrap" style={{ color: colors.text }} data-testid="guest-message">
                              {selectedGuest.guest_message}
                            </p>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                  
                  {selectedGuest.notes && (
                    <div className="mt-6 flex items-center justify-center gap-2" style={{ color: colors.textSecondary }}>
                      <Utensils className="h-4 w-4" />
                      <span data-testid="guest-notes">{selectedGuest.notes}</span>
                    </div>
                  )}

                  {/* Your view of the day — Share a moment (shown on both first visit & re-entry) */}
                  {(photoPhase === 'idle' || photoPhase === 'uploading') && uploadCount < 3 && (
                    <div
                      className="mt-10 mb-2 text-center"
                      style={{
                        opacity: shareHintAnimateNow ? 0 : 1,
                        animation: shareHintAnimateNow ? 'shareHintRise 900ms cubic-bezier(0.2, 0.7, 0.3, 1) 1200ms forwards' : undefined,
                      }}
                      data-testid="share-moment-block"
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={handleFileSelected}
                        data-testid="photo-file-input"
                      />
                      <p
                        className="text-[13px] tracking-wide mb-2"
                        style={{ color: colors.textMuted, opacity: 0.65 }}
                        data-testid="share-moment-header"
                      >
                        {t('yourViewOfTheDay')}
                      </p>
                      <button
                        onClick={handleShareMoment}
                        className="inline-flex items-center gap-2 text-[15px] font-medium transition-opacity hover:opacity-75"
                        style={{ color: colors.text, opacity: 0.85 }}
                        data-testid="share-moment-btn"
                      >
                        <Camera className="h-4 w-4" style={{ opacity: 0.85 }} />
                        <span>{uploadCount > 0 ? t('shareAnotherMoment') : t('shareAMoment')} &rarr;</span>
                      </button>
                      <button
                        onClick={handleShareSaveForLater}
                        className="block mx-auto text-[14px] mt-2 italic transition-opacity hover:opacity-70"
                        style={{ color: colors.textMuted, opacity: 0.6 }}
                        data-testid="save-link-helper"
                      >
                        {t('saveLinkToReturnLater')}
                      </button>
                      <style>{`@keyframes shareHintRise { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
                    </div>
                  )}

                  {/* Limit reached — quiet keepsake message (replaces share block after 3 uploads) */}
                  {photoPhase === 'limit' && (
                    <div
                      className="mt-10 mb-2 text-center"
                      data-testid="share-moment-limit-block"
                    >
                      <p
                        className="text-[13px] tracking-wide mb-3 font-light"
                        style={{ color: colors.textMuted, opacity: 0.6 }}
                      >
                        {t('yourViewOfTheDay')}
                      </p>
                      <p
                        className="text-[20px] font-medium leading-snug"
                        style={{ color: colors.text, opacity: 0.9 }}
                      >
                        {t('momentsShared')}
                      </p>
                      <p
                        className="text-[14px] mt-2 italic"
                        style={{ color: colors.textMuted, opacity: 0.6 }}
                      >
                        {t('savedForThem')}
                      </p>
                    </div>
                  )}

                  {/* Not you? — only on auto-recognized re-entry */}
                  <div className="mt-8 text-center space-y-4">
                    {isReturning && showNotYou && (
                      <button
                        onClick={handleNotYou}
                        className="text-[11px] transition-opacity"
                        style={{ color: colors.textMuted, opacity: 0.4 }}
                        data-testid="not-you-btn"
                      >
                        Not {selectedGuest.full_name.split(' ')[0]}?
                      </button>
                    )}
                  </div>

                  {/* Photo Preview Overlay */}
                  {photoPhase === 'preview' && photoPreview && (
                    <div
                      className="fixed inset-0 z-50 bg-black/95 flex flex-col p-5"
                      style={{
                        paddingTop: 'max(20px, env(safe-area-inset-top, 0px))',
                        paddingBottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
                      }}
                      data-testid="photo-preview-overlay"
                    >
                      <div className="w-full max-w-sm mx-auto flex-1 flex items-center justify-center min-h-0">
                        <img
                          src={photoPreview}
                          alt=""
                          className="max-w-full max-h-full rounded-lg object-contain"
                        />
                      </div>
                      <div className="w-full max-w-sm mx-auto mt-4">
                        <input
                          type="text"
                          value={photoNote}
                          onChange={e => setPhotoNote(e.target.value)}
                          placeholder="A few words, if you want"
                          className="w-full px-4 py-3 rounded-lg bg-white/10 text-white text-sm placeholder:text-white/30 outline-none border border-white/10 focus:border-white/30"
                          data-testid="photo-note-input"
                        />
                        <div className="mt-3 flex gap-3">
                          <button
                            onClick={handleUsePhoto}
                            className="flex-1 py-3 rounded-lg bg-white text-black text-sm font-medium"
                            data-testid="photo-use-btn"
                          >
                            Use photo &rarr;
                          </button>
                          <button
                            onClick={handleRetake}
                            className="px-4 py-3 rounded-lg text-white/60 text-sm border border-white/10"
                            data-testid="photo-retake-btn"
                          >
                            Retake
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Uploading state */}
                  {photoPhase === 'uploading' && (
                    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    </div>
                  )}

                  {/* Ephemeral floating counter — uploads 1 & 2 — shown on dark overlay */}
                  {photoPhase === 'counter' && (
                    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center px-8">
                      <p
                        className="text-white text-base font-light tracking-wide text-center"
                        style={{
                          animation: 'counterFloat 2000ms ease forwards',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                        data-testid="moment-counter-float"
                      >
                        {uploadCount} of 3 moments &mdash; for them
                      </p>
                      <style>{`
                        @keyframes counterFloat {
                          0% { opacity: 0; transform: translateY(6px); }
                          15% { opacity: 1; transform: translateY(0); }
                          85% { opacity: 1; transform: translateY(0); }
                          100% { opacity: 0; transform: translateY(-2px); }
                        }
                      `}</style>
                    </div>
                  )}

                  {/* Closing message — after the 3rd photo — shown on dark overlay */}
                  {photoPhase === 'closing' && (
                    <div className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center gap-2 px-8">
                      <p
                        className="text-white text-lg font-light tracking-wide text-center"
                        style={{ animation: 'closingFade 4500ms ease forwards' }}
                        data-testid="moment-closing-line-1"
                      >
                        That&rsquo;s enough for tonight
                      </p>
                      <p
                        className="text-white/55 text-sm font-light italic text-center"
                        style={{
                          animation: 'closingFade 4500ms ease forwards',
                          animationDelay: '120ms',
                          opacity: 0,
                        }}
                        data-testid="moment-closing-line-2"
                      >
                        The rest is for living.
                      </p>
                      <style>{`
                        @keyframes closingFade {
                          0% { opacity: 0; transform: translateY(6px); }
                          9% { opacity: 1; transform: translateY(0); }
                          87% { opacity: 1; transform: translateY(0); }
                          100% { opacity: 0; transform: translateY(-2px); }
                        }
                      `}</style>
                    </div>
                  )}
                </div>
              )}

              {/* Initial State */}
              {!selectedGuest && !notFound && searchQuery.length < 2 && (
                <p className="text-center mt-4" style={{ color: colors.textMuted }}>
                  {t('startTyping')}
                </p>
              )}
            </>
          ) : (
            /* Table Guests View */
            <div 
              className="backdrop-blur-xl rounded-2xl p-6" 
              style={{ backgroundColor: colors.cardBackground, border: `1px solid ${colors.primary}40` }}
              data-testid="table-guests-view"
            >
              {/* Header with Back Button */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={handleBackToSearch}
                  className="flex items-center gap-2 transition-colors"
                  style={{ color: colors.textSecondary }}
                  data-testid="back-to-search-btn"
                >
                  <ArrowLeft className="h-5 w-5" />
                  <span>{t('back')}</span>
                </button>
                <button
                  onClick={handleBackToSearch}
                  className="transition-colors"
                  style={{ color: colors.textSecondary }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Table Name Header */}
              <div className="mb-6">
                {/* Check if floor plan exists */}
                {tables.some(t => t.position_x !== null) ? (
                  /* With Floor Plan - title on left, button on right */
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-1 flex flex-col">
                        <div 
                          className="flex items-center justify-center gap-2 font-mono text-xl font-bold px-4 py-3 rounded-xl"
                          style={{ backgroundColor: colors.tableButton, color: colors.tableButtonText }}
                        >
                          <Users className="h-5 w-5" />
                          {selectedGuest?.table_name?.toLowerCase().includes('table') 
                            ? selectedGuest?.table_name 
                            : `Table ${selectedGuest?.table_name}`}
                        </div>
                        <p className="text-sm text-center mt-2" style={{ color: colors.textSecondary }}>
                          {tableGuests.length} {language === 'pt' ? 'convidados' : language === 'fr' ? 'invités' : language === 'es' ? 'invitados' : language === 'ru' ? 'гостей' : 'guests'}
                        </p>
                      </div>
                      <button
                        onClick={() => setShowFloorPlanPage(true)}
                        className="flex items-center justify-center gap-2 px-4 h-[50px] rounded-xl font-medium transition-all hover:scale-105 self-start"
                        style={{ 
                          backgroundColor: `${colors.primary}20`, 
                          color: colors.primary,
                          border: `1px solid ${colors.primary}40`
                        }}
                        data-testid="floor-plan-btn"
                      >
                        <Map className="h-5 w-5" />
                        <span className="text-sm">Floor Plan</span>
                      </button>
                    </div>
                  </>
                ) : (
                  /* No Floor Plan - title centered */
                  <div className="text-center">
                    <div 
                      className="inline-flex items-center gap-2 font-mono text-2xl font-bold px-6 py-3 rounded-xl mb-3"
                      style={{ backgroundColor: colors.tableButton, color: colors.tableButtonText }}
                    >
                      <Users className="h-6 w-6" />
                      {selectedGuest?.table_name?.toLowerCase().includes('table') 
                        ? selectedGuest?.table_name 
                        : `Table ${selectedGuest?.table_name}`}
                    </div>
                    <p className="text-sm" style={{ color: colors.textSecondary }}>
                      {tableGuests.length} {language === 'pt' ? 'convidados' : language === 'fr' ? 'invités' : language === 'es' ? 'invitados' : language === 'ru' ? 'гостей' : 'guests'}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Guest List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {tableGuests.map((guest, index) => {
                  const isCurrentGuest = guest.full_name === selectedGuest?.full_name;
                  return (
                    <div
                      key={index}
                      className={`p-4 rounded-xl transition-colors ${isCurrentGuest ? 'flex flex-col' : 'flex items-center gap-3'}`}
                      style={{ 
                        backgroundColor: isCurrentGuest ? `${colors.primary}20` : `${colors.border}40`,
                        border: isCurrentGuest ? `1px solid ${colors.primary}40` : 'none'
                      }}
                      data-testid={`table-guest-${index}`}
                    >
                      <div className={`flex items-center gap-3 ${isCurrentGuest ? 'w-full' : ''}`}>
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: isCurrentGuest ? `${colors.primary}30` : colors.border }}
                        >
                          <User className="h-5 w-5" style={{ color: isCurrentGuest ? colors.primary : colors.textMuted }} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium" style={{ color: isCurrentGuest ? colors.primary : colors.text }}>
                            {guest.full_name}
                            {isCurrentGuest && (
                              <span className="ml-2 text-xs" style={{ color: `${colors.primary}99` }}>
                                ({language === 'pt' ? 'tu' : language === 'fr' ? 'toi' : language === 'es' ? 'tú' : language === 'it' ? 'tu' : language === 'el' ? 'εσύ' : language === 'ru' ? 'ты' : 'you'})
                            </span>
                          )}
                        </p>
                        {guest.notes && (
                          <p className="text-sm flex items-center gap-1 mt-1" style={{ color: colors.textMuted }}>
                            <Utensils className="h-3 w-3" />
                            {guest.notes}
                          </p>
                        )}
                      </div>
                      </div>
                      
                      {/* Split Action Bar - Only on highlighted guest card */}
                      {isCurrentGuest && (
                        <>
                          <WishActionBar
                            slug={slug}
                            guestName={guest.full_name}
                            guestId={selectedGuest?.guest_id}
                            tableName={selectedGuest?.table_name}
                            colors={colors}
                            language={language}
                          />
                          {/* Leave it in your voice — secondary, intentional, theme-colored */}
                          <div className="mt-3 pl-1">
                            {voiceExisting ? (
                              <button
                                onClick={() => setVoicePlayOpen(true)}
                                className="text-[13px] italic transition-opacity hover:opacity-70"
                                style={{ color: colors.primary, opacity: 0.7 }}
                                data-testid="voice-existing-btn"
                              >
                                In your voice
                              </button>
                            ) : (
                              <button
                                onClick={handleOpenVoiceModal}
                                className="text-[13px] transition-opacity hover:opacity-70"
                                style={{ color: colors.primary }}
                                data-testid="leave-voice-btn"
                              >
                                Leave it in your voice &rarr;
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ─── In your voice — Recording Modal ─────────────────── */}
      {voiceOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-6"
          style={{
            backgroundColor: 'rgba(0,0,0,0.92)',
            paddingTop: 'max(20px, env(safe-area-inset-top, 0px))',
            paddingBottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
            animation: 'voiceModalFade 280ms ease',
          }}
          data-testid="voice-modal"
        >
          {/* Quiet ✕ exit (bottom subtle) */}
          <button
            onClick={handleCloseVoiceModal}
            className="absolute top-5 right-5 text-white/30 hover:text-white/60 transition-opacity"
            data-testid="voice-modal-close"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="w-full max-w-sm flex flex-col items-center text-center">
            {/* Header — only visible in idle (pre-record) */}
            {voicePhase === 'idle' && (
              <>
                <p className="text-white text-xl font-light italic mb-12" style={{ letterSpacing: '0.01em' }}>
                  In your voice
                </p>
                <button
                  onClick={handleStartRecording}
                  className="w-full py-6 rounded-full bg-white/10 hover:bg-white/15 active:bg-white/20 border border-white/15 text-white text-base font-light transition-all"
                  style={{ letterSpacing: '0.02em' }}
                  data-testid="voice-tap-to-speak"
                >
                  Tap to speak
                </button>
                <p className="text-white/40 text-sm font-light italic mt-8">
                  Just as it comes
                </p>
              </>
            )}

            {/* Recording — breathing ring, no timer, full-width tappable to stop */}
            {voicePhase === 'recording' && (
              <button
                onClick={handleStopRecording}
                className="w-full flex flex-col items-center"
                data-testid="voice-recording"
              >
                {/* Breathing ring */}
                <div className="relative w-32 h-32 flex items-center justify-center mb-10">
                  <span
                    className="absolute inset-0 rounded-full"
                    style={{ border: '1px solid rgba(255,255,255,0.6)', animation: 'voiceBreath 2400ms ease-out infinite' }}
                  />
                  <span
                    className="absolute inset-0 rounded-full"
                    style={{ border: '1px solid rgba(255,255,255,0.4)', animation: 'voiceBreath 2400ms ease-out 800ms infinite' }}
                  />
                  <span
                    className="block w-3 h-3 rounded-full"
                    style={{ backgroundColor: 'rgba(255,255,255,0.85)', boxShadow: '0 0 18px rgba(255,255,255,0.4)' }}
                  />
                </div>
                <p className="text-white/55 text-sm font-light tracking-wide">
                  Tap to stop
                </p>
                {/* "A breath left" cue near 50s — fades in/out */}
                {voiceCue && (
                  <p
                    className="text-white/65 text-sm font-light italic mt-6"
                    style={{ animation: 'voiceCueFade 2400ms ease forwards' }}
                    data-testid="voice-cue"
                  >
                    A breath left
                  </p>
                )}
              </button>
            )}

            {/* Review — silence + 3 buttons (no headline, per spec) */}
            {voicePhase === 'review' && (
              <div className="w-full flex flex-col items-center gap-5" data-testid="voice-review">
                <div className="flex items-center justify-center gap-6 w-full">
                  <button
                    onClick={handleReRecord}
                    className="px-6 py-3 rounded-full text-white/70 hover:text-white text-sm font-light tracking-wide transition-colors border border-white/15"
                    data-testid="voice-rerecord"
                  >
                    Re-record
                  </button>
                  <button
                    onClick={handleSendVoice}
                    className="px-8 py-3 rounded-full bg-white text-black text-sm font-medium tracking-wide transition-transform active:scale-[0.98]"
                    data-testid="voice-send"
                  >
                    Send
                  </button>
                </div>
                <button
                  onClick={handleCloseVoiceModal}
                  className="text-white/30 hover:text-white/55 text-xs tracking-wide mt-2 transition-colors"
                  data-testid="voice-discard"
                >
                  Discard
                </button>
              </div>
            )}

            {/* Sending — quiet spinner */}
            {voicePhase === 'sending' && (
              <div className="flex flex-col items-center" data-testid="voice-sending">
                <div className="w-5 h-5 border-2 border-white/20 border-t-white/70 rounded-full animate-spin" />
              </div>
            )}

            {/* Sent — confirmation fades */}
            {voicePhase === 'sent' && (
              <p
                className="text-white/75 text-base font-light italic"
                style={{ animation: 'voiceSentFade 1400ms ease forwards' }}
                data-testid="voice-sent"
              >
                Sent
              </p>
            )}

            {/* Mic denied — quiet inline */}
            {voicePhase === 'mic-denied' && (
              <div className="flex flex-col items-center gap-3" data-testid="voice-mic-denied">
                <p className="text-white/60 text-sm font-light italic">Microphone needed</p>
                <button
                  onClick={handleStartRecording}
                  className="text-white/85 text-sm underline-offset-4 hover:underline"
                >
                  Try again
                </button>
              </div>
            )}
          </div>

          <style>{`
            @keyframes voiceModalFade { from { opacity: 0; } to { opacity: 1; } }
            @keyframes voiceBreath {
              0% { transform: scale(0.6); opacity: 0; }
              30% { opacity: 0.55; }
              100% { transform: scale(1.6); opacity: 0; }
            }
            @keyframes voiceCueFade {
              0% { opacity: 0; transform: translateY(4px); }
              20% { opacity: 1; transform: translateY(0); }
              80% { opacity: 1; }
              100% { opacity: 0; transform: translateY(-2px); }
            }
            @keyframes voiceSentFade {
              0% { opacity: 0; }
              25% { opacity: 1; }
              80% { opacity: 1; }
              100% { opacity: 0; }
            }
          `}</style>
        </div>
      )}

      {/* ─── In your voice — Playback Modal (locked, no edit/delete) ─── */}
      {voicePlayOpen && voiceExisting && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center px-6"
          style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
          data-testid="voice-playback-modal"
        >
          <button
            onClick={() => setVoicePlayOpen(false)}
            className="absolute top-5 right-5 text-white/30 hover:text-white/60 transition-opacity"
            data-testid="voice-playback-close"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <VoicePlaybackInline
            audioUrl={`${process.env.REACT_APP_BACKEND_URL}${voiceExisting.audio_url}`}
            duration={voiceExisting.duration_sec || 0}
            label="Your message"
          />
        </div>
      )}
    </div>
  );
}

// Inline player used inside the locked-message playback modal — minimal, no edit/delete.
function VoicePlaybackInline({ audioUrl, duration, label }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const fmt = (s) => {
    const m = Math.floor((s || 0) / 60);
    const r = Math.max(0, Math.floor((s || 0) % 60));
    return `${m}:${r.toString().padStart(2, '0')}`;
  };
  const toggle = () => {
    const a = ref.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); return; }
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };
  return (
    <div className="w-full max-w-sm flex flex-col items-center text-center">
      <p className="text-white/85 text-base font-light italic mb-10">{label}</p>
      <button
        onClick={toggle}
        className="w-20 h-20 rounded-full bg-white text-black flex items-center justify-center text-2xl transition-transform active:scale-95"
        data-testid="voice-play-toggle"
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? '❘❘' : '▶'}
      </button>
      <p className="text-white/35 text-xs font-light tracking-wide mt-5">{fmt(duration)}</p>
      <audio
        ref={ref}
        src={audioUrl}
        onEnded={() => setPlaying(false)}
        preload="metadata"
      />
    </div>
  );
}

export default function PublicGuestSearch() {
  return (
    <LanguageProvider defaultLanguage="en">
      <GuestSearchContent />
    </LanguageProvider>
  );
}
